'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useRef } from 'react'

import { cn } from '@/lib/utils'

const ROLL_EASE = [0.2, 0.8, 0.2, 1] as const

const toNumber = (text: string) => Number(text.replace(/[^\d.-]/g, ''))

/**
 * Odometer-style text: each digit that changes rolls in from below when the number rises and
 * from above when it falls; separators and the digits that stay put don't move. For live
 * figures that tick (latency, prices) — the eye follows the change instead of a flicker.
 */
export function RollingNumber({ text, className }: { text: string; className?: string }) {
  const previousRef = useRef(text)
  const directionRef = useRef(1)
  if (text !== previousRef.current) {
    directionRef.current = toNumber(text) >= toNumber(previousRef.current) ? 1 : -1
    previousRef.current = text
  }
  const chars = text.split('')

  return (
    <span className={cn('inline-flex items-baseline', className)} aria-label={text}>
      {chars.map((char, index) => {
        // Keyed from the right, so a digit keeps its place when the number gains a digit.
        const key = chars.length - index
        if (!/\d/.test(char)) {
          return (
            <span key={key} aria-hidden="true">
              {char}
            </span>
          )
        }
        return <RollingDigit key={key} char={char} direction={directionRef.current} />
      })}
    </span>
  )
}

function RollingDigit({ char, direction }: { char: string; direction: number }) {
  const versionRef = useRef(0)
  const lastRef = useRef(char)
  if (lastRef.current !== char) {
    lastRef.current = char
    versionRef.current++
  }

  return (
    <span aria-hidden="true" className="relative inline-block overflow-hidden align-bottom">
      <AnimatePresence initial={false} mode="popLayout" custom={direction}>
        <motion.span
          key={versionRef.current}
          custom={direction}
          variants={{
            enter: (d: number) => ({ y: d > 0 ? '70%' : '-70%', opacity: 0 }),
            center: { y: 0, opacity: 1 },
            exit: (d: number) => ({ y: d > 0 ? '-70%' : '70%', opacity: 0 }),
          }}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.22, ease: ROLL_EASE }}
          className="inline-block"
        >
          {char}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}
